import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AgnesAlert,
  AgnesAlertContent,
  AgnesAlertTitle,
  AgnesAutocomplete,
  AgnesBadge,
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesPageHeader,
  AgnesStatusBadge,
} from '@agnes/ui';
import { AlertTriangle, FileText, Info, Mail, MailOpen, Paperclip, RefreshCw } from 'lucide-react';
import { type Aba, useMensagensEcacStore } from '@/stores/useMensagensEcacStore';
import { useEmpregadoresStore } from '@/stores/useEmpregadoresStore';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

type Any = any;

interface Mensagem {
  [k: string]: unknown;
  id: string;
  descricaoOrigem: string;
  assunto: string;
  recebida: string;
  validade: string;
  lida: boolean;
  altaRelevancia: boolean;
  temAnexos: boolean;
}

const ABAS: { key: Aba; label: string }[] = [
  { key: 'todas', label: 'Todas' },
  { key: 'nao-lidas', label: 'Não lidas' },
  { key: 'lidas', label: 'Lidas' },
];

function StatCard({
  title,
  value,
  caption,
  accent,
}: {
  title: React.ReactNode;
  value: number;
  caption: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border border-agnes-border bg-agnes-bg-surface p-5 ${accent ? 'border-t-4 border-t-agnes-success' : ''}`}
    >
      <div className="flex items-start justify-between">
        <p className="text-agnes-text">{title}</p>
        {accent && <Info className="h-4 w-4 shrink-0 text-agnes-primary" />}
      </div>
      <p className="my-2 text-4xl font-bold text-agnes-text">{value}</p>
      <p className="text-sm text-agnes-text-muted">{caption}</p>
    </div>
  );
}

export default function PageMensagensEcac() {
  const [searchParams] = useSearchParams();

  const cnpjCliente = useMensagensEcacStore((s) => s.cnpjCliente);
  const aba = useMensagensEcacStore((s) => s.aba);
  const lista = useMensagensEcacStore((s) => s.lista);
  const total = useMensagensEcacStore((s) => s.total);
  const page = useMensagensEcacStore((s) => s.page);
  const size = useMensagensEcacStore((s) => s.size);
  const sortBy = useMensagensEcacStore((s) => s.sortBy);
  const sortOrder = useMensagensEcacStore((s) => s.sortOrder);
  const setSort = useMensagensEcacStore((s) => s.setSort);
  const dashboard = useMensagensEcacStore((s) => s.dashboard);
  const carregando = useMensagensEcacStore((s) => s.carregando);
  const sincronizando = useMensagensEcacStore((s) => s.sincronizando);
  const empregadores = useEmpregadoresStore((s) => s.lista);

  React.useEffect(() => {
    useEmpregadoresStore.getState().buscar();
    const cliente = searchParams.get('cliente');
    if (cliente) useMensagensEcacStore.getState().selecionarEmpregador(cliente);
  }, [searchParams]);

  const empLabel = (e: Any) =>
    `${e.company ?? e.socialName ?? e.fantasyName ?? ''} - ${e.cnpjFilial}`;
  const empSel = empregadores.find((e: Any) => e.cnpjFilial === cnpjCliente);

  const columns: AgnesDataTableColumn<Mensagem>[] = [
    { key: 'descricaoOrigem', header: 'Origem', width: 200, sortable: true },
    {
      key: 'assunto',
      header: 'Assunto',
      width: 380,
      sortable: true,
      render: (m) => (
        <div className="flex items-center gap-2">
          {m.lida ? (
            <MailOpen className="h-4 w-4 shrink-0 text-agnes-text-muted" />
          ) : (
            <Mail className="h-4 w-4 shrink-0 text-agnes-primary" />
          )}
          <span className={m.lida ? '' : 'font-semibold'}>{m.assunto}</span>
          {m.temAnexos && <Paperclip className="h-3.5 w-3.5 shrink-0 text-agnes-text-muted" />}
          {m.altaRelevancia && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" />}
        </div>
      ),
    },
    { key: 'recebida', header: 'Recebida', width: 150, sortable: true },
    { key: 'validade', header: 'Validade', width: 120, sortable: true },
    {
      key: 'relevancia',
      header: 'Relevância',
      width: 120,
      render: (m) => (
        <AgnesStatusBadge
          status={m.altaRelevancia ? 'warning' : 'info'}
          label={m.altaRelevancia ? 'Alta' : 'Normal'}
          dot
        />
      ),
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<FileText />}
        title="Mensagens e-CAC"
        subtitle="Caixa postal do e-CAC (SERPRO) - mensagens da Receita Federal por empregador"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Encargos' },
          { label: 'Mensagens e-CAC' },
        ]}
      />

      <AgnesAlert
        preset="warning"
        className="mb-4 rounded-xl bg-agnes-warning-light"
        data-qa="ecac-indisponivel-banner"
      >
        <AgnesAlertTitle>Instabilidade no sistema e-CAC</AgnesAlertTitle>
        <AgnesAlertContent>
          A coleta de Mensagens e-CAC pode estar indisponível. As consultas à caixa postal da
          Receita Federal estão sendo recusadas; quando isso ocorre, a sincronização falha e nenhuma
          mensagem nova é trazida.
        </AgnesAlertContent>
      </AgnesAlert>

      <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          accent
          title={
            <>
              Total de <b>mensagens</b>
            </>
          }
          value={dashboard?.total ?? 0}
          caption="mensagens coletadas da caixa postal"
        />
        <StatCard
          title={
            <>
              Mensagens <b>não lidas</b>
            </>
          }
          value={dashboard?.naoLidas ?? 0}
          caption="ainda não visualizadas"
        />
        <StatCard
          title={
            <>
              Alta <b>relevância</b>
            </>
          }
          value={dashboard?.altaRelevancia ?? 0}
          caption="marcadas como alta relevância pela RFB"
        />
      </div>

      <AgnesCard className="my-4">
        <AgnesCardContent className="flex items-stretch">
          <div className="flex flex-row items-end justify-between gap-4">
            <AgnesAutocomplete
              label="Empregador"
              placeholder="Buscar por CNPJ ou razão social"
              options={empregadores}
              value={empSel ? [empSel] : []}
              onOptionToggle={(e: Any) =>
                useMensagensEcacStore.getState().selecionarEmpregador(e.cnpjFilial)
              }
              getOptionLabel={empLabel}
              isOptionEqualToValue={(a: Any, b: Any) => a.cnpjFilial === b.cnpjFilial}
              noOptionsText="Nenhum empregador"
            />
            <AgnesButton
              variant="primary"
              disabled={!cnpjCliente || sincronizando}
              onClick={() => useMensagensEcacStore.getState().sincronizar()}
              startIcon={<RefreshCw className="h-4 w-4" />}
            >
              {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
            </AgnesButton>
          </div>

          <div className="mb-2 flex items-center gap-2 border-b border-agnes-border">
            {ABAS.map((a) => (
              <button
                key={a.key}
                onClick={() => useMensagensEcacStore.getState().mudarAba(a.key)}
                className={`px-4 py-2 text-sm ${aba === a.key ? 'border-b-2 border-agnes-primary font-semibold text-agnes-primary' : 'text-agnes-text-muted'}`}
              >
                {a.label}
              </button>
            ))}
            {total > 0 && <AgnesBadge>{total} mensagem(ns)</AgnesBadge>}
          </div>

          <TableWrapper>
            <AgnesDataTable<Mensagem>
              columns={columns}
              data={lista}
              loading={carregando}
              rowKey={(m) => String(m.id)}
              sort={{ sortBy, sortOrder, onSortChange: setSort }}
              pagination={{
                page,
                pageSize: size,
                total,
                serverSide: true,
                onPageChange: (p: number) => useMensagensEcacStore.getState().setPage(p),
                onPageSizeChange: (s: number) => useMensagensEcacStore.getState().setSize(s),
              }}
            />
          </TableWrapper>
        </AgnesCardContent>
      </AgnesCard>
    </>
  );
}
